<template>
    <button type="button" v-print="printObj" class="px-6 py-2 rounded-full whitespace-nowrap text-center font-medium text-white bg-primary">
        {{ $t('button.print_receipt') }}
    </button>

    <div id="receipt" class="modal">
        <div class="modal-dialog max-w-[302px] rounded-none" id="print">
            <div class="modal-body">
                <div class="text-center pb-3.5 border-b border-dashed border-gray-400">
                    <h3 class="font-bold mb-1">{{ company.company_name }}</h3>
                    <h4 class="text-sm font-normal">{{ company.company_address }}</h4>
                    <h5 class="text-sm font-normal">{{ $t('label.tel') }}: {{ company.company_calling_code }} {{
                        company.company_phone
                    }}</h5>
                </div>

                <table class="w-full my-1.5">
                    <tbody>
                        <tr>
                            <td class="text-xs text-left py-0.5 text-heading">
                                #{{ order.order_serial_no }}
                            </td>
                        </tr>
                        <tr>
                            <td class="text-xs text-left py-0.5 text-heading">{{ order.order_date }}</td>
                            <td class="text-xs text-right py-0.5 text-heading">{{ order.order_time }} {{ $t('label.hrs') }}</td>
                        </tr>
                    </tbody>
                </table>

                <table class="w-full">
                    <thead class="border-t border-b border-dashed border-gray-400">
                        <tr>
                            <th scope="col" class="py-1 font-normal text-xs flex items-center justify-start gap-2 text-heading">
                                <span>{{ $t('label.qty') }}</span>
                                <span>{{ $t('label.product_description') }}</span>
                            </th>
                            <th scope="col" class="py-1 font-normal text-xs text-right text-heading w-8">
                                {{ $t('label.price') }}
                            </th>
                        </tr>
                    </thead>

                    <tbody class="border-b border-dashed border-gray-400">
                        <tr v-if="orderProducts.length > 0" v-for="product in orderProducts" :key="product">
                            <td class="flex justify-start items-start gap-2 text-left font-normal align-top py-1">
                                <div class="text-xs leading-4 text-heading">{{ product.quantity }}</div>
                                <div class="flex flex-col items-start justify-start">
                                    <p class="text-xs leading-4 text-heading">{{ product.product_name }}
                                    </p>
                                    <p v-if="product.variation_names" class="text-xs leading-4 text-heading max-w-[200px]">
                                        {{ product.variation_names }}
                                    </p>
                                    <p class="text-xs leading-4 text-heading" v-if="product.product_tax.length > 0"
                                        v-for="tax in product.product_tax" :key="tax">
                                        {{ tax.tax_name }} ({{ tax.tax_rate }}%)
                                    </p>
                                </div>
                            </td>
                            <td class="text-right font-normal align-top py-1">
                                <p class="text-xs leading-4 text-heading">{{ product.subtotal_currency_price }}</p>
                            </td>
                        </tr>
                    </tbody>
                </table>

                <div class="w-full py-2">
                    <table class="w-full">
                        <tbody>
                            <tr>
                                <td class="text-xs text-left py-0.5 text-heading">{{ $t('label.subtotal') }}:
                                </td>
                                <td class="text-xs text-right py-0.5 text-heading">
                                    {{ order.subtotal_currency_price }}
                                </td>
                            </tr>
                            <tr>
                                <td class="text-xs text-left py-0.5 text-heading">
                                    {{ $t('label.tax_fee') }}:
                                </td>
                                <td class="text-xs text-right py-0.5 text-heading">
                                    {{ order.tax_currency_price }}
                                </td>
                            </tr>
                            <tr>
                                <td class="text-xs text-left py-0.5 text-heading">{{ $t('label.discount') }}:
                                </td>
                                <td class="text-xs text-right py-0.5 text-heading">{{ order.discount_currency_price }}
                                </td>
                            </tr>
                            <tr v-if="order.order_type === enums.orderTypeEnum.DELIVERY">
                                <td class="text-xs text-left py-0.5 text-heading">{{
                                    $t('label.shipping_charge')
                                    }}:
                                </td>
                                <td class="text-xs text-right py-0.5 text-heading">{{
                                    order.shipping_charge_currency_price
                                    }}
                                </td>
                            </tr>
                            <tr v-if="order.shipping_quote && order.shipping_quote.provider">
                                <td class="text-xs text-left py-0.5 text-heading">{{ $t('label.shipping_provider') }}:</td>
                                <td class="text-xs text-right py-0.5 text-heading">{{ order.shipping_quote.provider }}</td>
                            </tr>
                            <tr v-if="order.shipping_quote && order.shipping_quote.service_level">
                                <td class="text-xs text-left py-0.5 text-heading">{{ $t('label.shipping_service') }}:</td>
                                <td class="text-xs text-right py-0.5 text-heading">{{ order.shipping_quote.service_level }}</td>
                            </tr>
                            <tr v-if="order.shipping_quote && order.shipping_quote.days">
                                <td class="text-xs text-left py-0.5 text-heading">{{ $t('label.shipping_days') }}:</td>
                                <td class="text-xs text-right py-0.5 text-heading">{{ order.shipping_quote.days }}</td>
                            </tr>
                            <tr v-if="order.shipping_shipment && order.shipping_shipment.tracking_number">
                                <td class="text-xs text-left py-0.5 text-heading">{{ $t('label.shipping_tracking_number') }}:</td>
                                <td class="text-xs text-right py-0.5 text-heading">{{ order.shipping_shipment.tracking_number }}</td>
                            </tr>
                            <tr v-if="order.shipping_shipment && order.shipping_shipment.label_url">
                                <td class="text-xs text-left py-0.5 text-heading">{{ $t('label.shipping_label') }}:</td>
                                <td class="text-xs text-right py-0.5 text-heading">{{ order.shipping_shipment.label_url }}</td>
                            </tr>

                            <tr>
                                <td class="text-xs text-left py-0.5 font-bold text-heading">
                                    {{ $t('label.total') }}:
                                </td>
                                <td class="text-xs text-right py-0.5 font-bold text-heading">
                                    {{ order.total_currency_price }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                
                <div class="text-xs py-2 border-t border-b border-dashed border-gray-400 text-heading">
                    <table>
                        <tbody>
                            <tr>
                                <td class="pr-1"> {{ $t('label.order_type') }}:</td>
                                <td class="">{{ enums.orderTypeEnumArray[order.order_type] }}</td>
                            </tr>
                            <tr>
                                <td class="pr-1">{{ $t('label.payment_type') }}:</td>
                                <td class="">{{ order.payment_method_name }}</td>
                            </tr>
                            <tr>
                                <td class="pr-1">{{ $t('label.order_date_time') }}:</td>
                                <td class="">{{ order.order_datetime }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div class="text-xs py-2 border-b border-dashed border-gray-400 text-heading">
                    <table>
                        <tbody v-if="order.order_type === enums.orderTypeEnum.DELIVERY">
                            <tr>
                                <td class="pr-1">{{ $t('label.customer') }}:</td>
                                <td class="">{{ orderAddress.full_name }}</td>
                            </tr>
                            <tr>
                                <td class="pr-1">{{ $t('label.phone') }}:</td>
                                <td class="">{{ orderAddress.country_code + '' + orderAddress.phone }}</td>
                            </tr>
                            <tr>
                                <td class="pr-1">{{ $t('label.address') }}:</td>
                                <td class="">
                                    <span v-if="orderAddress.address">{{ orderAddress.address }},</span>
                                    <span v-if="orderAddress.city">{{ orderAddress.city }},</span>
                                    <span v-if="orderAddress.state">{{ orderAddress.state }},</span>
                                    <span v-if="orderAddress.country">{{ orderAddress.country }},</span>
                                    <span v-if="orderAddress.zip_code">{{ orderAddress.zip_code }}</span>
                                </td>
                            </tr>
                        </tbody>
                        <tbody v-if="order.order_type === enums.orderTypeEnum.PICK_UP">
                            <tr>
                                <td class="pr-1">{{ $t('label.customer') }}:</td>
                                <td class="">{{ orderUser.name }}</td>
                            </tr>
                            <tr>
                                <td class="pr-1">{{ $t('label.phone') }}:</td>
                                <td class="">{{ orderUser.country_code + '' + orderUser.phone }}</td>
                            </tr>
                            <tr>
                                <td class="pr-1">{{ $t('label.outlet') }}:</td>
                                <td class="">
                                    <span v-if="outletAddress.address">{{ outletAddress.address }},</span>
                                    <span v-if="outletAddress.state">{{ outletAddress.state }},</span>
                                    <span v-if="outletAddress.city">{{ outletAddress.city }},</span>
                                    <span v-if="outletAddress.country">{{ outletAddress.country }},</span>
                                    <span v-if="outletAddress.zip_code">{{ outletAddress.zip_code }}</span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <div class="text-center pt-2 pb-4">
                    <p class="text-[11px] leading-[14px] text-heading">
                        {{ $t('message.thank_you') }}
                    </p>
                    <p class="text-[11px] leading-[14px] text-heading">
                        {{ $t('message.please_come_again') }}
                    </p>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import print from "vue3-print-nb";
import orderTypeEnum from "../../../../enums/modules/orderTypeEnum";
import paymentTypeEnum from "../../../../enums/modules/paymentTypeEnum";

export default {
    name: "OrderReceiptComponent",
    props: {
        order: Object,
        orderProducts: Object,
        orderUser: Object,
        orderAddress: Object
    },
    directives: {
        print
    },
    data() {
        return {
            loading: {
                isActive: false
            },
            printObj: {
                id: "print",
                popTitle: this.$t("menu.order_receipt"),
            },
            enums: {
                orderTypeEnum: orderTypeEnum,
                orderTypeEnumArray: {
                    [orderTypeEnum.DELIVERY]: this.$t("label.delivery"),
                    [orderTypeEnum.PICK_UP]: this.$t("label.pick_up")
                },
            }
        }
    },
    mounted() {
        this.$store.dispatch("company/lists").then().catch();
    },
    computed: {
        company: function () {
            return this.$store.getters['company/lists'];
        },
        outletAddress: function () {
            return this.$store.getters['frontendOrder/outletAddress'];
        },
    }
}
</script>